import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  questionSetApi,
  type ComprehensionQuestion,
  type QuestionSet,
  type Verdict,
} from '@/lib/api/comprehension';

/**
 * Offered after an accepted submission: two or three inputs chosen for this
 * student's actual solution, and the question of what their own code prints for
 * each.
 *
 * This is the only signal in the platform that can see a student who asked an
 * assistant for the answer and typed it out by hand — paste telemetry reads
 * that as composed, similarity finds nothing because the code is theirs alone,
 * and no published AI-authorship classifier is reliable on a thirty-line
 * solution.
 *
 * Several questions rather than one because a single answer is guessable. A
 * live model asked for revealing inputs to two-sum proposed [[3,3],6] and
 * [[5,5,5,5],10] with sound reasoning for each — and both return [0,1]. The
 * set is deduplicated by answer server-side for that reason, and passing means
 * getting all of them right.
 *
 * Nothing here fails anyone. Skipping is free and costs only that the solve
 * stays unaccounted for; a wrong answer shows them what their program actually
 * does.
 */
const POLL_MS = 4000;
/** ~4 minutes.
 *
 *  This was 22 polls (~90s), chosen against a 55s measurement. Production was
 *  then measured at 72s on td-debug-ticket-counter — a 16s margin, which is
 *  not a margin. The cap exists to stop a permanently stuck server costing a
 *  request every four seconds, not to impose a deadline on a model that is
 *  working; it should sit far above real latency, not just above it. */
const MAX_POLLS = 60;

/**
 * Which problems have already had their unprompted reminder.
 *
 * A solve that just landed always opens the dialog: the student is right
 * there, and the questions are about the code still on their screen. A solve
 * from an earlier visit gets ONE unprompted reminder, ever.
 *
 * The difference matters because the dialog is now mounted on every visit to
 * a solved problem. Without this, a student who skipped — or who simply
 * reloaded the page — was handed the same modal again on every single load.
 * Reported within an hour of shipping the mount fix, and fairly: a reminder
 * that cannot be got rid of is a nag, and nagging is exactly the wrong tone
 * for a feature whose whole promise is that skipping is free.
 *
 * Re-submitting still brings the questions straight back, so nothing is lost
 * by not asking twice.
 */
const REMINDED_KEY = 'kp-comprehension-reminded';

function remindedSlugs(): string[] {
  try {
    const raw = localStorage.getItem(REMINDED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === 'string') : [];
  } catch {
    // Private mode, quota, or a value some other build wrote. Falling back to
    // "not yet reminded" costs at most one extra dialog; throwing here would
    // take down the workspace.
    return [];
  }
}

function mayAutoOpen(slug: string, justSolved: boolean): boolean {
  return justSolved || !remindedSlugs().includes(slug);
}

function markReminded(slug: string, justSolved: boolean) {
  if (justSolved) return; // a fresh solve is always allowed to open
  try {
    const next = [...new Set([...remindedSlugs(), slug])].slice(-200);
    localStorage.setItem(REMINDED_KEY, JSON.stringify(next));
  } catch {
    // See remindedSlugs: storage being unavailable must not break the dialog.
  }
}

export function ExplainSolveDialog({
  slug,
  justSolved = false,
}: {
  slug: string;
  /** True when the solve landed in this session. Only the wording depends on
   *  it — the dialog is also mounted for a solve from an earlier visit, where
   *  congratulating the student on an acceptance they got days ago would read
   *  as a bug. */
  justSolved?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // Preparation takes about half a minute — a reasoning model plus a judge
  // round trip. Bounded so a server that reports "preparing" forever degrades
  // to a quiet no-op instead of an endless 4-second poll; production did
  // exactly that for a problem whose prerequisite row was missing.
  const [polls, setPolls] = useState(0);
  const gaveUp = polls >= MAX_POLLS;

  const { data: result } = useQuery({
    queryKey: ['comprehension-set', slug],
    queryFn: async () => {
      const r = await questionSetApi.get(slug);
      setPolls((n) => (r.status === 'preparing' ? n + 1 : 0));
      return r;
    },
    refetchInterval: (query) =>
      query.state.data?.status === 'preparing' && !gaveUp ? POLL_MS : false,
    retry: false,
  });

  const set = result?.status === 'ready' ? result.set : undefined;
  // Shown while the question is being written. Without this the student sits
  // through half a minute of nothing after an accepted submission and
  // reasonably concludes the feature does not exist.
  const preparing = result?.status === 'preparing' && !gaveUp;
  // Gave up waiting while the server was still writing. Distinct from "nothing
  // to ask": the questions are coming, we simply stopped asking for them. The
  // student is owed that sentence — the previous version unmounted the whole
  // dialog here, so a wait that ran long ended as a blank screen.
  const stalled = result?.status === 'preparing' && gaveUp;
  const outstanding = set?.questions.filter((q) => !q.answered) ?? [];

  // Whether this dialog has ever been shown, which is NOT the same as whether
  // there is something outstanding.
  //
  // Without it, answering the last question unmounts the dialog the instant the
  // answer lands — so the student never learns whether they were right, never
  // sees what their code actually prints, and the most useful moment in the
  // whole feature is destroyed by a re-render. Caught in the browser, not by a
  // test.
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (outstanding.length > 0 && mayAutoOpen(slug, justSolved)) {
      markReminded(slug, justSolved);
      setOpen(true);
      setEngaged(true);
    }
  }, [outstanding.length, slug, justSolved]);

  // Open on the *start* of preparation rather than on its completion, so the
  // acknowledgement of the solve is immediate and the wait is visible.
  useEffect(() => {
    if (preparing && mayAutoOpen(slug, justSolved)) {
      markReminded(slug, justSolved);
      setOpen(true);
    }
  }, [preparing, slug, justSolved]);

  // Nothing to ask and nothing was asked: stay out of the way.
  if (!set && !preparing && !stalled) return null;
  if (set && outstanding.length === 0 && !engaged) return null;

  if (!set && stalled) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Still being written</DialogTitle>
            <DialogDescription>
              Your questions are taking longer than usual. They are still being
              prepared on our side, and they will be waiting for you the next
              time you open this problem — nothing is lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-between">
            <span className="self-center text-xs text-muted-foreground">
              The solve counts either way.
            </span>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (!set) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {justSolved ? 'Nice — accepted.' : 'Questions about your solution'}
            </DialogTitle>
            <DialogDescription>
              Writing a couple of questions about your own code. This takes
              a few seconds.
            </DialogDescription>
          </DialogHeader>
          {/* A skeleton of the question card that is coming, rather than a
              spinner: it shows the shape of what is being prepared, so the
              wait reads as "your questions are being written" instead of
              "something is happening". */}
          <div
            className="max-h-[55vh] space-y-4 overflow-y-auto pr-1"
            role="status"
            aria-label="Preparing your questions"
          >
            {[0, 1].map((i) => (
              <div key={i} className="rounded-lg border border-border p-4">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="mt-3 h-3 w-40" />
                <Skeleton className="mt-2 h-16 w-full" />
                <Skeleton className="mt-3 h-3 w-36" />
                <Skeleton className="mt-2 h-16 w-full" />
              </div>
            ))}
          </div>

          {/* The same footer the answered state shows. Skipping is as free
              during the wait as it is after it, and saying so is what stops
              the student feeling held. */}
          <DialogFooter className="sm:justify-between">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Skip for now
            </Button>
            <span className="self-center text-xs text-muted-foreground">
              Skipping is fine — the solve still counts, it just stays unaccounted for.
            </span>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {outstanding.length === 0
              ? 'All answered'
              : justSolved
                ? 'Nice — accepted. Want to lock this one in?'
                : 'Want to lock this solve in?'}
          </DialogTitle>
          <DialogDescription>
            {outstanding.length === 0
              ? 'Have a look at what your code actually prints for each of these.'
              : outstanding.length === 1
                ? 'One question about your own code. Answer it and this solve counts as fully accounted for.'
                : `${outstanding.length} short questions about your own code. Answer them and this solve counts as fully accounted for.`}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
          {set.questions.map((q, i) => (
            <QuestionRow
              key={q.position}
              slug={slug}
              question={q}
              index={i}
              total={set.questions.length}
              onAnswered={() => queryClient.invalidateQueries({ queryKey: ['comprehension-set', slug] })}
            />
          ))}
        </div>

        <DialogFooter className="sm:justify-between">
          {outstanding.length === 0 ? (
            <>
              <span className="self-center text-xs text-muted-foreground">{summarise(set)}</span>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Skip for now
              </Button>
              <span className="self-center text-xs text-muted-foreground">
                Skipping is fine — the solve still counts, it just stays unaccounted for.
              </span>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The line under a completed set.
 *
 * States the count plainly and never scolds. Getting these wrong is the most
 * useful outcome available — it is the moment a student discovers they did not
 * know what their own program does — so the wording points at the code rather
 * than at them.
 */
function summarise(set: QuestionSet): string {
  const total = set.questions.length;
  const right = set.questions.filter((q) => q.correct).length;
  if (right === total) return `All ${total} right — this solve is now accounted for.`;
  if (right === 0) return `None right this time — worth a proper read back through your solution.`;
  return `${right} of ${total} right. Worth stepping through the others.`;
}

/**
 * One question, answered and graded on its own.
 *
 * Per question rather than all at once: a student who gets the first right and
 * the second wrong should see that as it happens, and submitting three answers
 * blind then being told "two of three" teaches much less.
 */
function QuestionRow({
  slug,
  question,
  index,
  total,
  onAnswered,
}: {
  slug: string;
  question: ComprehensionQuestion;
  index: number;
  total: number;
  onAnswered: () => void;
}) {
  const [answer, setAnswer] = useState('');
  const [verdict, setVerdict] = useState<Verdict | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () => questionSetApi.answer(slug, question.position, answer),
    onSuccess: (v) => {
      setVerdict(v);
      onAnswered();
    },
  });

  const settled = verdict !== null || question.answered;
  const wasCorrect = verdict?.correct ?? question.correct;

  return (
    <div className="rounded-md border p-3">
      <p className="mb-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        Question {index + 1} of {total}
      </p>
      {question.kind === 'choice' ? (
        <p className="mb-2 text-sm leading-snug">{question.prompt}</p>
      ) : (
        <>
          <p className="mb-1 text-xs font-medium text-muted-foreground">
            Your code was run with this input
          </p>
          <pre className="mb-2 max-h-24 overflow-auto rounded bg-muted p-2 font-mono text-xs">
            {question.input}
          </pre>
        </>
      )}

      {settled ? (
        <div className="space-y-2">
          <p
            className={
              wasCorrect
                ? 'text-sm font-medium text-green-700 dark:text-green-400'
                : 'text-sm font-medium text-amber-700 dark:text-amber-400'
            }
          >
            {/*
              Kind-aware, because "step through your code with this input" is
              nonsense for a question that has no input — and telling a student
              to do something impossible is how a feature stops being trusted.
            */}
            {wasCorrect
              ? "That's it."
              : question.kind === 'choice'
                ? 'Not quite — worth a second look at that part of your code.'
                : 'Not quite — worth stepping through your code with this input.'}
          </p>
          {/*
            The real output is shown either way. Getting this wrong is the most
            useful thing that can happen here — it is the moment a student finds
            out they did not know what their own program does, and withholding
            the answer would waste it.
          */}
          {verdict && (
            <div>
              <p className="mb-1 text-xs font-medium text-muted-foreground">
                {question.kind === 'choice' ? 'The right answer' : 'What your code actually prints'}
              </p>
              <pre className="max-h-24 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs">
                {verdict.expected_output}
              </pre>
            </div>
          )}
          {/*
            Why this input was chosen, shown only after answering. Before, it
            would be a hint; after, it is the explanation of what the question
            was getting at.
          */}
          {question.rationale && (
            <p className="text-[11px] leading-snug text-muted-foreground">
              Why this one: {question.rationale.toLowerCase()}
            </p>
          )}
        </div>
      ) : (
        <>
          {question.kind === 'choice' ? (
            /*
              Radio group rather than free text. The model writes the question
              and marks the answer; a student picking an option means grading is
              an index comparison, so nothing has to judge whether an
              explanation was good enough.
            */
            <fieldset className="mb-2 space-y-1.5">
              <legend className="sr-only">{question.prompt}</legend>
              {(question.options ?? []).map((opt, oi) => (
                <label
                  key={oi}
                  className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm hover:bg-muted"
                >
                  <input
                    type="radio"
                    name={`q-${question.position}`}
                    value={String(oi)}
                    checked={answer === String(oi)}
                    onChange={() => setAnswer(String(oi))}
                    className="mt-1"
                  />
                  <span className="leading-snug">{opt}</span>
                </label>
              ))}
            </fieldset>
          ) : (
            <>
              <label
                htmlFor={`answer-${question.position}`}
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                What does your code print?
              </label>
              <Textarea
                id={`answer-${question.position}`}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Type the exact output…"
                className="mb-2 font-mono text-xs"
                rows={2}
              />
            </>
          )}
          <Button size="sm" onClick={() => mutate()} disabled={!answer.trim() || isPending}>
            {isPending ? 'Checking…' : 'Check'}
          </Button>
        </>
      )}
    </div>
  );
}
